// Übergaben in der Hausmeister-App (Ticket #436).
//
// Der Hausmeister sieht nur, was ihm das Büro freigegeben hat (uebergabe_hausmeister); die
// Datenbank gibt ohnehin nichts anderes heraus. Er startet die Übergabe auf seinem Telefon:
// Der Entwurf entsteht lokal (der Assistent arbeitet offline), mit Objekt, Einheit, Mieter
// und Art aus der Kopfzeile des Büros — und, falls das Büro eine Vorlage gewählt hat, mit
// Räumen, Zählern und Schlüsseln daraus.
import { supabase } from '../supabase/client';
import { ladeEntwurf } from '../uebergabe/lokal';
import { erzeugeAusVorlage } from '../uebergabe/vorlage';
import { ladeServerStand } from '../uebergabe/sync';
import type { Bezug } from '../uebergabe/bezug';
import type { Art, Protokoll } from '../uebergabe/typen';
import { erfasstText, istBegonnen, parteienText, uebergabeStatus, type UebergabeInhalt, type UebergabeStatus } from './uebergabeAnzeige';

export interface HausmeisterUebergabe {
  protokollId: string;
  vorlageId: string | null;
  art: Art;
  status: string;
  /** Abschluss angestoßen, PDF noch nicht da — z. B. Funkloch mitten im Abschluss. */
  abschlussOffen: boolean;
  datum: string | null;
  objekt: string;
  adresse: string;
  einheit: string | null;
  mieter: string | null;
  bezug: Bezug;
  /** Anzeige (E3): Geplant / In Erfassung / Abschluss offen */
  anzeigeStatus: UebergabeStatus;
  /** „Übergeber → Übernehmer“ */
  parteien: string;
  /** Kurzfassung des Erfassten */
  erfasst: string;
}

interface Zeile {
  protokoll_id: string;
  vorlage_id: string | null;
  protokoll: {
    id: string; art: Art; status: string; pdf_path: string | null; durchgefuehrt_am: string | null;
    parteien: Protokoll['parteien'] | null; raeume: Protokoll['raeume'] | null; zaehler: Protokoll['zaehler'] | null;
    unterschriften: Protokoll['unterschriften'] | null; bemerkungen: string | null;
    objekt_id: string | null; einheit_id: string | null; mieter_id: string | null; eigentuemer_id: string | null;
    objekt: { bezeichnung: string | null; strasse: string | null; hausnummer: string | null; plz: string | null; stadt: string | null } | null;
    einheit: { bezeichnung: string | null } | null;
    mieter: { vorname: string | null; nachname: string | null; firma_name: string | null } | null;
  } | null;
}

const eins = <T,>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? v[0] ?? null : v ?? null);

export async function ladeMeineUebergaben(meineId: string): Promise<HausmeisterUebergabe[]> {
  if (!meineId) return [];
  const { data, error } = await supabase.from('uebergabe_hausmeister')
    .select(`protokoll_id, vorlage_id, protokoll:uebergabeprotokolle!uebergabe_hausmeister_protokoll_id_fkey(
      id, art, status, pdf_path, durchgefuehrt_am, objekt_id, einheit_id, mieter_id, eigentuemer_id,
      parteien, raeume, zaehler, unterschriften, bemerkungen,
      objekt:objekte(bezeichnung, strasse, hausnummer, plz, stadt),
      einheit:objekt_einheiten(bezeichnung),
      mieter:mieter(vorname, nachname, firma_name))`)
    .eq('hausmeister_id', meineId);
  if (error) { console.error('Übergaben laden fehlgeschlagen', error.message); return []; }

  const liste: HausmeisterUebergabe[] = [];
  for (const z of (data || []) as unknown as Zeile[]) {
    const p = eins(z.protokoll);
    if (!p) continue;
    const abschlussOffen = p.status === 'abgeschlossen' && !p.pdf_path;
    if (p.status !== 'in_erfassung' && !abschlussOffen) continue;
    const o = eins(p.objekt);
    const m = eins(p.mieter);
    // Ein Entwurf auf diesem Gerät ist der neuere Stand – dann zählt sein Inhalt.
    const entwurf = await ladeEntwurf(p.id).catch(() => null);
    const inhalt: UebergabeInhalt = entwurf?.protokoll ?? {
      parteien: p.parteien ?? [], raeume: p.raeume ?? [], zaehler: p.zaehler ?? [],
      unterschriften: p.unterschriften ?? [], bemerkungen: p.bemerkungen,
    };
    liste.push({
      protokollId: p.id,
      vorlageId: z.vorlage_id,
      art: p.art,
      status: p.status,
      abschlussOffen,
      datum: p.durchgefuehrt_am,
      objekt: o?.bezeichnung || 'Objekt',
      adresse: [[o?.strasse, o?.hausnummer].filter(Boolean).join(' '), [o?.plz, o?.stadt].filter(Boolean).join(' ')].filter(Boolean).join(', '),
      einheit: eins(p.einheit)?.bezeichnung ?? null,
      mieter: m ? (m.firma_name || [m.vorname, m.nachname].filter(Boolean).join(' ')) : null,
      bezug: {
        art: p.art, objekt_id: p.objekt_id, einheit_id: p.einheit_id,
        mieter_id: p.mieter_id, eigentuemer_id: p.eigentuemer_id, kaeufer_eigentuemer_id: null,
      } as Bezug,
      anzeigeStatus: uebergabeStatus(abschlussOffen, istBegonnen(inhalt, !!entwurf)),
      parteien: parteienText(inhalt.parteien),
      erfasst: erfasstText(inhalt),
    });
  }
  return liste.sort((a, b) => (a.datum || '').localeCompare(b.datum || ''));
}

/**
 * Vor dem Öffnen: Liegt auf diesem Telefon schon ein Entwurf, bleibt er unangetastet — sonst
 * ginge laufende Arbeit verloren. Ohne Entwurf und mit Vorlage wird er aus der Vorlage
 * erzeugt; ohne Vorlage legt der Assistent ihn selbst aus dem Bezug an.
 * Liefert die Zahl der Vorlagen-Fotos, die sich nicht laden ließen.
 */
export async function bereiteUebergabeVor(
  u: HausmeisterUebergabe,
  userProfile: { id?: string | null; full_name?: string | null; email?: string | null } | null | undefined,
): Promise<number> {
  if (await ladeEntwurf(u.protokollId)) return 0;
  if (!u.vorlageId) return 0;
  // Hat das Büro am PC schon vorbereitet (Ticket #488), gilt dieser Stand — der Assistent
  // holt ihn beim Öffnen. Die Vorlage ist nur der Startpunkt für eine leere Übergabe.
  const stand = await ladeServerStand(u.protokollId).catch(() => null);
  if (stand?.hatInhalt) return 0;
  const { fehlendeBilder } = await erzeugeAusVorlage(u.vorlageId, u.art, userProfile, { protokollId: u.protokollId, bezug: u.bezug });
  return fehlendeBilder;
}

export const ART_TEXT: Record<Art, string> = { einzug: 'Einzug', auszug: 'Auszug', kauf: 'Übergabe an Eigentümer' };
